import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import styled from 'styled-components';
import { animated, config, useTransition } from '@react-spring/web';
import { APPEAR_TRANSITION_DURATION } from './constants';
import { isDefined } from '../../helpers/defined';
import { zIndex } from '../../style/z-index';
import { EMPTY_PROPS } from '../../helpers';

// transformation offset, changes from 1 (full offset) to 0 (no offset)
const getSlideTransform = (placement, offset = 0) => {
  if (!placement) {
    return 'translate(0, 0)';
  }
  if (placement.startsWith('top')) {
    return `translate(0, ${24 * offset}px)`;
  }
  if (placement.startsWith('bottom')) {
    return `translate(0, -${24 * offset}px)`;
  }
  if (placement.startsWith('left')) {
    return `translate(${24 * offset}px, 0)`;
  }
  if (placement.startsWith('right')) {
    return `translate(-${24 * offset}px, 0)`;
  }
  return 'translate(0, 0)';
};
const getZoomTransform = (placement, offset = 0) => {
  return `scale(${(1 - offset) * 0.2 + 0.8})`;
};
const getTransformOrigin = placement => {
  switch (placement) {
    case 'top':
      return 'center bottom';
    case 'top-start':
      return 'left bottom';
    case 'top-end':
      return 'right bottom';
    case 'bottom':
    case 'auto':
      return 'center top';
    case 'bottom-start':
    case 'auto-start':
      return 'left top';
    case 'bottom-end':
    case 'auto-end':
      return 'right top';
    case 'left':
      return 'left center';
    case 'left-start':
      return 'left top';
    case 'left-end':
      return 'left bottom';
    case 'right':
      return 'right center';
    case 'right-start':
      return 'right top';
    case 'right-end':
      return 'right bottom';
    default:
      return 'left top';
  }
};
const WrapperContainer = styled(animated.div)`
  display: flex;
  flex-direction: column;
  will-change: transform, opacity;
  position: relative;
  ${({
  $zIndex
}) => isDefined($zIndex) && zIndex($zIndex)}
`;
const AppearTransition = /*#__PURE__*/React.forwardRef(({
  placement = 'bottom',
  show = false,
  duration = APPEAR_TRANSITION_DURATION,
  style = EMPTY_PROPS,
  config: configProp = EMPTY_PROPS,
  children,
  type = 'slide',
  zIndex,
  ...restProps
}, forwardedRef) => {
  const transitions = useTransition(show, {
    from: {
      opacity: 0,
      offset: 1
    },
    enter: {
      opacity: 1,
      offset: 0
    },
    leave: {
      opacity: 0,
      offset: 1
    },
    config: {
      ...config.gentle,
      duration,
      ...configProp
    }
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, transitions((props, item) => item ? /*#__PURE__*/React.createElement(WrapperContainer, _extends({
    ref: forwardedRef,
    $zIndex: zIndex,
    style: {
      opacity: props.opacity,
      //@ts-ignore
      pointerEvents: props.opacity.to(o => o < 1 ? 'none' : 'auto'),
      ...(type === 'slide' ? {
        transform: props.offset.to(offset => getSlideTransform(placement, offset))
      } : type === 'zoom' ? {
        transform: props.offset.to(offset => getZoomTransform(placement, offset)),
        transformOrigin: getTransformOrigin(placement)
      } : {}),
      ...style
    }
  }, restProps), children) : null));
});
export default AppearTransition;